import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Card from "components/DesignSystem/Card/Card";
import Table from "components/DesignSystem/Table/V2/Table";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { REVENUE_BASIS } from "constants/revenueMetrics";
import React, { ChangeEvent, ReactNode } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { TableData } from "types/Models/revenueMetrics";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { Search } from "components/DesignSystem/Search/Search";
import { debounce } from "utils/debouncing";

export const FinalDataTable = ({
  finalTableData,
  revenueBasis,
  onRevenueBasisChange,
  onSearchChange,
  pagination,
}: {
  finalTableData: TableData[];
  revenueBasis: keyof typeof REVENUE_BASIS;
  onRevenueBasisChange: (revenueBasis: keyof typeof REVENUE_BASIS) => void;
  onSearchChange?: (search: string) => void;
  pagination: ReactNode;
}) => {
  const handleSearch = debounce((e: ChangeEvent<HTMLInputElement>) => {
    onSearchChange?.(e.target.value);
  });
  const createColumn = createColumnHelper<TableData>();

  const { isMobile } = useWindowSize();

  const allPossibleColumns = finalTableData[0]?.periodic_revenue || [];

  const monthlyColumns = allPossibleColumns.map((period) => {
    return createColumn.accessor(`periodic_revenue`, {
      size: 114,
      header: () => <div className="t-text-end">{period.period.range}</div>,
      id: period.period.start_date,
      enableSorting: true,
      cell: ({ row: { original } }) => {
        const amount = original.periodic_revenue.find(
          (x) => x.period.range === period.period.range
        )?.customer_total_amount;

        return (
          <div className="t-text-end">
            <AmountSuperScript amount={Number(amount)} />
          </div>
        );
      },
    });
  });

  const columns = [
    createColumn.accessor("item_info.name", {
      size: isMobile ? 140 : 280,
      header: "NAME",
      id: "name",
      enableSorting: true,
      cell: (row) => {
        return row.getValue();
      },
    }),
    ...monthlyColumns,
  ];

  const table = useReactTable({
    data: finalTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: isMobile ? {} : { left: ["name"] },
    },
  });

  return (
    <Card.Root borderRounded="lg" className="t-gap-0" variant="secondary">
      <Card.Header>
        <Card.Title>Revenue Breakdown</Card.Title>
        <ToggleGroup
          defaultValue="CUSTOMER"
          onValueChange={onRevenueBasisChange}
          value={revenueBasis}
        >
          <ToggleGroupItem value="CUSTOMER" aria-label="Customers">
            Customers
          </ToggleGroupItem>
          <ToggleGroupItem value="PRODUCT" aria-label="Products">
            Products
          </ToggleGroupItem>
        </ToggleGroup>
      </Card.Header>
      {onSearchChange && (
        <div className="t-px-5 sm:t-px-4 t-pt-6 t-flex t-items-center t-justify-between sm:t-flex-col sm:t-items-stretch sm:t-gap-3">
          <Search onChange={handleSearch} placeholder="Search" />
          {pagination}
        </div>
      )}
      {!onSearchChange && (
        <div className="t-px-5 sm:t-px-4 t-pt-6 t-flex t-justify-end">
          {pagination}
        </div>
      )}
      <div className="t-px-5 sm:t-px-4 t-pb-5 t-pt-4 t-w-full">
        <Table.Container className="t-overflow-scroll scrollbar-visible t-scroll-auto">
          <Table.Content className="t-w-full">
            <Table.Head>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.HeadRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    const styles = getCommonPinningStyles(header.column);
                    const isPinnedColumn = header.column.getIsPinned();
                    const width = { width: header.column.getSize() };
                    return (
                      <Table.HeadCell
                        key={header.id}
                        style={{
                          ...styles,
                          minWidth: header.column.getSize(),
                          ...(isPinnedColumn && width),
                        }}
                        className={classNames({
                          "!t-bg-surface-lighter-grey t-drop-shadow-table-left-pin":
                            isPinnedColumn,
                        })}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Table.HeadCell>
                    );
                  })}
                </Table.HeadRow>
              ))}
            </Table.Head>
            <Table.Body>
              {table.getRowModel().rows.map((row) => {
                return (
                  <Table.Row key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      const styles = getCommonPinningStyles(cell.column);
                      const isCellPinned = cell.column.getIsPinned();
                      const width = { width: cell.column.getSize() };
                      return (
                        <Table.Cell
                          key={cell.id}
                          style={{
                            ...styles,
                            minWidth: cell.column.getSize(),
                            ...(isCellPinned && width),
                          }}
                          className={classNames({
                            "t-drop-shadow-table-left-pin": isCellPinned,
                          })}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      </div>
    </Card.Root>
  );
};
